import { View } from 'web-mojo';
import { LocationClient } from 'web-mojo/map';
import { Location } from '@portal/models/Location.js';

/**
 * LocationFormView — Add / Edit a location with live address autocomplete.
 *
 * Address field uses LocationClient to:
 *   - Show Google Places suggestions as the user types
 *   - Auto-fill latitude, longitude, and timezone on selection
 *
 * Emits 'saved' with the saved Location model data when complete.
 */
export default class LocationFormView extends View {
    constructor(options = {}) {
        super(options);
        this._client = new LocationClient();
        this._suggestions = [];
        this._showSuggestions = false;
        this._resolving = false; // true while fetching place details + timezone
    }

    async onAfterRender() {
        await super.onAfterRender();
        this._bindAddressInput();
    }

    _bindAddressInput() {
        const input = this.element?.querySelector('[name=address]');
        if (!input) return;

        let debounce;
        input.addEventListener('input', () => {
            clearTimeout(debounce);
            const q = input.value.trim();
            if (q.length < 3) {
                this._suggestions = [];
                this._showSuggestions = false;
                this._updateSuggestions();
                return;
            }
            debounce = setTimeout(async () => {
                const result = await this._client.autocomplete(q);
                this._suggestions = result?.data || result?.suggestions || result?.predictions || [];
                this._showSuggestions = this._suggestions.length > 0;
                this._updateSuggestions();
            }, 220);
        });

        input.addEventListener('blur', () => {
            // Delay so click on suggestion registers first
            setTimeout(() => {
                this._suggestions = [];
                this._showSuggestions = false;
                this._updateSuggestions();
            }, 200);
        });
    }

    _updateSuggestions() {
        const box = this.element?.querySelector('[data-container="suggestions"]');
        if (!box) return;
        if (!this._showSuggestions || this._suggestions.length === 0) {
            box.innerHTML = '';
            return;
        }
        box.innerHTML = this._suggestions.map((s, i) => `
            <div class="list-group-item list-group-item-action py-2 px-3 small"
                style="cursor:pointer;" data-action="pick-suggestion" data-index="${i}">
                <i class="bi bi-geo-alt text-muted me-2"></i>${s.description || s.formatted_address || s.text}
            </div>
        `).join('');
    }

    async onActionPickSuggestion(event, el) {
        const index = parseInt(el.getAttribute('data-index'));
        const suggestion = this._suggestions[index];
        if (!suggestion) return;

        this._resolving = true;
        this._suggestions = [];
        this._showSuggestions = false;
        this._updateSuggestions();

        const placeId = suggestion.place_id || suggestion.id;
        const details = await this._client.placeDetails({
            place_id: placeId,
            session_token: this._client.sessionToken,
        });

        const place = details?.address || details;
        const lat = place?.latitude;
        const lng = place?.longitude;
        const address = place?.formatted_address || suggestion.description;

        // Set address field
        const addressEl = this.element.querySelector('[name=address]');
        if (addressEl) addressEl.value = address || '';

        // Set lat/lng fields
        const latEl = this.element.querySelector('[name=latitude]');
        const lngEl = this.element.querySelector('[name=longitude]');
        if (latEl) latEl.value = lat != null ? lat : '';
        if (lngEl) lngEl.value = lng != null ? lng : '';

        // Auto-detect timezone from coordinates
        if (lat && lng) {
            const tzResult = await this._client.timezone({ lat, lng });
            const tzEl = this.element.querySelector('[name=timezone]');
            if (tzEl && tzResult?.timezone_id) {
                tzEl.value = tzResult.timezone_id;
            }
        }

        this._resolving = false;
    }

    async onActionSave() {
        const name = this.element.querySelector('[name=name]')?.value.trim();
        if (!name) {
            app.toast.warning('Name is required');
            return false;
        }

        const payload = {
            name,
            address: this.element.querySelector('[name=address]')?.value.trim() || '',
            latitude: parseFloat(this.element.querySelector('[name=latitude]')?.value) || null,
            longitude: parseFloat(this.element.querySelector('[name=longitude]')?.value) || null,
            timezone: this.element.querySelector('[name=timezone]')?.value.trim() || 'UTC',
            contact_name: this.element.querySelector('[name=contact_name]')?.value.trim() || '',
            contact_phone: this.element.querySelector('[name=contact_phone]')?.value.trim() || '',
            contact_email: this.element.querySelector('[name=contact_email]')?.value.trim() || '',
            access_instructions: this.element.querySelector('[name=access_instructions]')?.value.trim() || '',
            requires_signature: this.element.querySelector('[name=requires_signature]')?.checked || false,
        };

        const isNew = !this.model?.get('id');
        if (isNew) {
            payload.group = app.activeGroup.id;
        }

        app.showLoading();
        const model = this.model || new Location();
        const resp = await model.save(payload);
        app.hideLoading();

        if (!resp?.data?.status) {
            app.toast.error(resp?.data?.error || 'Failed to save location');
            return false;
        }

        const saved = resp.data.data;
        app.toast.success(isNew ? 'Location created' : 'Location saved');
        if (!saved?.latitude || !saved?.longitude) {
            app.toast.warning('Geocoding failed — coordinates are missing. Enter them manually so stops appear on the operator\'s map.');
        }
        this.emit('saved', saved);
        return true;
    }

    _v(key) {
        return this.model?.get(key) || '';
    }

    set template(v) { this._t = v; }

    get template() {
        const v = key => this._v(key);
        const lat = this.model?.get('latitude');
        const lng = this.model?.get('longitude');

        return `
            <div class="p-3">
                <div class="mb-3">
                    <label class="form-label">Name *</label>
                    <input type="text" class="form-control" name="name" value="${v('name')}" placeholder="e.g. Central Depot">
                </div>

                <div class="mb-3">
                    <label class="form-label">Address</label>
                    <input type="text" class="form-control" name="address"
                        value="${v('address')}"
                        placeholder="Start typing an address…"
                        autocomplete="off">
                    <div class="list-group position-absolute z-3 w-100" style="max-width:480px;"
                        data-container="suggestions"></div>
                    <div class="row g-2 mt-1">
                        <div class="col-6">
                            <input type="text" class="form-control form-control-sm ${lat ? '' : 'border-warning'}"
                                name="latitude" value="${lat || ''}" placeholder="Latitude">
                        </div>
                        <div class="col-6">
                            <input type="text" class="form-control form-control-sm ${lng ? '' : 'border-warning'}"
                                name="longitude" value="${lng || ''}" placeholder="Longitude">
                        </div>
                    </div>
                    <div class="form-text ${lat && lng ? 'text-muted' : 'text-warning'}">
                        ${lat && lng
                            ? '<i class="bi bi-pin-map me-1"></i>Auto-filled. Edit if needed.'
                            : '<i class="bi bi-exclamation-triangle me-1"></i>No coordinates yet — auto-filled when you select an address, or enter manually.'}
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Timezone</label>
                    <select class="form-select" name="timezone">
                        ${[
                            ['UTC', 'UTC'],
                            ['America/New_York', 'Eastern (ET)'],
                            ['America/Chicago', 'Central (CT)'],
                            ['America/Denver', 'Mountain (MT)'],
                            ['America/Los_Angeles', 'Pacific (PT)'],
                            ['America/Phoenix', 'Arizona (no DST)'],
                            ['America/Anchorage', 'Alaska (AKT)'],
                            ['Pacific/Honolulu', 'Hawaii (HST)'],
                            ['Europe/London', 'London (GMT/BST)'],
                            ['Europe/Paris', 'Central Europe (CET)'],
                            ['Europe/Helsinki', 'Eastern Europe (EET)'],
                            ['Asia/Dubai', 'Dubai (GST)'],
                            ['Asia/Kolkata', 'India (IST)'],
                            ['Asia/Singapore', 'Singapore (SGT)'],
                            ['Asia/Tokyo', 'Tokyo (JST)'],
                            ['Australia/Sydney', 'Sydney (AEST)'],
                        ].map(([val, label]) =>
                            `<option value="${val}" ${(v('timezone') || 'UTC') === val ? 'selected' : ''}>${label}</option>`
                        ).join('')}
                    </select>
                    <div class="form-text text-muted">Auto-updated when you select an address.</div>
                </div>

                <div class="row g-3 mb-3">
                    <div class="col-6">
                        <label class="form-label">Contact Name</label>
                        <input type="text" class="form-control" name="contact_name" value="${v('contact_name')}">
                    </div>
                    <div class="col-6">
                        <label class="form-label">Contact Phone</label>
                        <input type="text" class="form-control" name="contact_phone" value="${v('contact_phone')}">
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Contact Email</label>
                    <input type="email" class="form-control" name="contact_email" value="${v('contact_email')}">
                </div>

                <div class="mb-3">
                    <label class="form-label">Access Instructions</label>
                    <textarea class="form-control" name="access_instructions" rows="3"
                        placeholder="e.g. Enter via side gate, code 1234">${v('access_instructions')}</textarea>
                </div>

                <div class="mb-4 form-check">
                    <input type="checkbox" class="form-check-input" name="requires_signature" id="req_sig"
                        ${this.model?.get('requires_signature') ? 'checked' : ''}>
                    <label class="form-check-label" for="req_sig">Requires Signature</label>
                </div>
            </div>
        `;
    }
}
